import {
    AuthenticationResult,
    BrowserAuthError,
    InteractionRequiredAuthError,
    InteractionStatus,
} from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import AppLogger from '@shared/logging/appLogger';
import { scopes } from '@shared/utilities/msal/msal.config';
import { useCallback } from 'react';

import User from '../types/user';

interface MsalUserHook {
    signOut: (useLegacyRedirect?: boolean) => void;
    signIn: () => Promise<void | AuthenticationResult>;
    isAuthenticated: boolean;
    getUser: () => User | undefined;
    getIdToken: () => Promise<string>;
    inProgress: InteractionStatus;
    redirectToLogin: () => Promise<void>;
}

const getLogoutUri = (auctionsHomePageUrl: string) => {
    if (!auctionsHomePageUrl || auctionsHomePageUrl === '') {
        return null;
    }
    let homepageUrl = auctionsHomePageUrl;
    if (!homepageUrl.endsWith('/')) {
        homepageUrl += '/';
    }
    return `${homepageUrl}login.aspx/logoutallapps`;
};

export default function useMsalUser() {
    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const signOut = async (useLegacyRedirect = true) => {
        return instance.logoutRedirect({
            postLogoutRedirectUri: useLegacyRedirect
                ? getLogoutUri(process.env.AUCTIONS_HOMEPAGE_URL || '')
                : '/',
        });
    };

    const getUser = useCallback(() => {
        if (!instance) return null;

        const accounts = instance.getAllAccounts();

        if (accounts?.length === 0) return null;

        const account = accounts[0];

        if (account && !instance.getActiveAccount()) {
            instance.setActiveAccount(account);
        }

        if (!account?.idTokenClaims) return null;

        const tokenClaims = account.idTokenClaims as { [key: string]: string };

        const user: User = {
            username: tokenClaims['username'],
            consumerResourceId: tokenClaims['consumerResourceId'],
        };

        if (tokenClaims['createTime']) {
            user.createTime = tokenClaims['createTime'];
        }

        return user;
    }, [instance]);

    const signIn = async () => {
        if (isAuthenticated || inProgress !== InteractionStatus.None) {
            return;
        }

        const isOnHomePage = window.location.href === `${process.env.AUCTIONS_HOMEPAGE_URL}/`;
        try {
            const accounts = instance.getAllAccounts();
            if (accounts.length === 0) {
                /**
                 * If a user is signing in from the auctions home page, they should be redirected to member dashboard following redirect.
                 * All other remotes should redirect back to same remote.
                 */
                return instance.loginRedirect({
                    scopes,
                    redirectStartPage: isOnHomePage
                        ? `${process.env.AUCTIONS_HOMEPAGE_URL}/memberdashboard`
                        : null,
                });
            } else {
                return instance.acquireTokenSilent({
                    scopes,
                });
            }
        } catch (error) {
            AppLogger.logException({
                error,
            });
        }
    };

    const acquireToken = async () => {
        if (!isAuthenticated || inProgress !== InteractionStatus.None) {
            throw new Error(`MSAL acquireToken called while InteractionStatus = '${inProgress}'`);
        }

        try {
            return await instance.acquireTokenSilent({
                scopes,
            });
        } catch (error) {
            if (
                error instanceof InteractionRequiredAuthError ||
                error instanceof BrowserAuthError
            ) {
                return await instance.acquireTokenRedirect({
                    scopes,
                    prompt: 'login',
                });
            } else {
                throw error;
            }
        }
    };

    const getIdToken = async () => {
        const response = (await acquireToken()) as AuthenticationResult;
        return response?.idToken;
    };

    const redirectToLogin = async () => {
        try {
            await instance.loginRedirect({
                scopes,
            });
        } catch (error) {
            AppLogger.logException({
                error,
            });
        }
    };

    const userHook: MsalUserHook = {
        signIn,
        signOut,
        isAuthenticated,
        getUser,
        getIdToken,
        inProgress,
        redirectToLogin,
    };

    return userHook;
}
